import { api, type OwnerAircraft, type OwnerOverview } from '../api';
import { errorCard, esc, fmtDate, fmtHours, fmtMargin, html, loading, pill, tile } from '../ui';

/**
 * The owner's portal.
 *
 * An owner is not a CAMO user with fewer buttons. They want four things, in
 * this order: can I fly it, when is it next unavailable, is the certificate
 * still valid, what am I being charged. So the page leads with a plain
 * yes-or-no per aircraft and the reason when the answer is no — not a status
 * chip that assumes the reader knows what ATTENTION means.
 *
 * Read-only throughout, and that is a property of the API rather than of this
 * file: an owner session is refused any write before it reaches the database.
 */

function aircraftCard(a: OwnerAircraft): string {
  const arcState =
    a.arc === null ? 'NA' : a.arc.daysLeft <= 0 ? 'OVERDUE' : a.arc.daysLeft <= 90 ? 'DUE_SOON' : 'OK';

  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>${esc(a.registration)}</h2>
          <p class="tiny dim">${esc(a.type)}${a.base ? ` · based ${esc(a.base)}` : ''}</p>
        </div>
        ${a.airworthy
          ? pill('OK', 'Available to fly')
          : pill('OVERDUE', 'Not available')}
      </div>

      ${!a.airworthy && a.reason
        ? html`<div class="card-b">
            <div class="callout c-exceed">
              <span class="cg" aria-hidden="true">✕</span>
              <div><b>${esc(a.registration)} cannot fly.</b> ${esc(a.reason)}</div>
            </div>
          </div>`
        : ''}

      <div class="kv">
        <div>
          <span class="lbl">Airframe</span>
          <b>${esc(fmtHours(a.tsnHours ?? 0))} h</b>
          <small>${a.landingsTotal.toLocaleString('en-GB')} landings</small>
        </div>
        <div>
          <span class="lbl">Certificate</span>
          <b style="color:var(--${arcState === 'OVERDUE' ? 'exceed' : arcState === 'DUE_SOON' ? 'caution' : 'ok'})">
            ${a.arc ? esc(a.arc.formKind.replace('FORM_', 'Form ').toLowerCase()) : 'none'}
          </b>
          <small>
            ${a.arc
              ? a.arc.daysLeft <= 0
                ? `expired ${esc(fmtDate(a.arc.expiresAt))}`
                : `expires ${esc(fmtDate(a.arc.expiresAt))} · ${a.arc.daysLeft} d`
              : 'not issued'}
          </small>
        </div>
        <div>
          <span class="lbl">Open items</span>
          <b>${a.openDefects.length}</b>
          <small>${a.openDefects.some((d) => d.severity === 'GROUNDING') ? 'one stops the aircraft' : 'none stopping it'}</small>
        </div>
        <div>
          <span class="lbl">Next visit</span>
          <b>${a.planned[0] ? esc(fmtDate(a.planned[0].plannedStart)) : '—'}</b>
          <small>${a.planned[0] ? `${a.planned[0].groundTimeDays ?? 1} day(s) on the ground` : 'nothing booked'}</small>
        </div>
      </div>

      ${a.nextDue.length
        ? html`
            <div class="rows">
              ${a.nextDue
                .map(
                  (d) => html`
                    <div class="row" style="border-left-color:var(--${d.state === 'OVERDUE' ? 'exceed' : 'caution'})">
                      <div class="bd">
                        <div class="t">${esc(d.title)}</div>
                        <div class="m">
                          ${esc(d.code ?? 'scheduled maintenance')}${d.when ? ` · around ${esc(fmtDate(d.when))}` : ''}
                        </div>
                      </div>
                      ${pill(
                        d.state === 'OVERDUE' ? 'OVERDUE' : 'DUE_SOON',
                        d.remaining === null ? '—' : fmtMargin(d.remaining, d.unit === 'HOURS' ? 'HOURS' : 'DAYS'),
                      )}
                    </div>
                  `,
                )
                .join('')}
            </div>
          `
        : ''}

      ${a.openDefects.length
        ? html`
            <div class="rows">
              ${a.openDefects
                .map(
                  (d) => html`
                    <div class="row" style="border-left-color:var(--${d.severity === 'GROUNDING' ? 'exceed' : 'caution'})">
                      <div class="bd">
                        <div class="t">${esc(d.description)}</div>
                        <div class="m">
                          ${esc(d.reference)}${d.melCategory ? ` · MEL ${esc(d.melCategory)}` : ''}${d.deferralLimitAt ? ` · rectify by ${esc(fmtDate(d.deferralLimitAt))}` : ''}
                          ${d.limitation ? `<br />${esc(d.limitation)}` : ''}
                        </div>
                      </div>
                      ${pill(d.severity === 'GROUNDING' ? 'OVERDUE' : 'DUE_SOON',
                        d.severity === 'GROUNDING' ? 'No flight' : 'Deferred')}
                    </div>
                  `,
                )
                .join('')}
            </div>
          `
        : ''}

      ${a.planned.length
        ? html`<div class="card-f">
            Booked in: ${esc(a.planned.map((p) => `${p.reference} — ${p.title} (${fmtDate(p.plannedStart)})`).join('; '))}
          </div>`
        : ''}
    </div>
  `;
}

function view(o: OwnerOverview): string {
  const money = (n: number) =>
    `${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${o.summary.currency}`;

  return html`
    <div class="page-h">
      <div>
        <h1>${esc(o.customer?.name ?? 'Your aircraft')}</h1>
        <p class="dim">
          Managed by your continuing airworthiness organisation. This is a read-only view — to change
          anything, speak to them.
        </p>
      </div>
    </div>

    <div class="tiles">
      ${tile({
        label: 'Available to fly',
        value: `${o.summary.airworthy} / ${o.summary.aircraft}`,
        sub: o.summary.unavailable ? `${o.summary.unavailable} not available` : 'all available',
        tone: o.summary.unavailable ? 'exceed' : 'ok',
      })}
      ${tile({
        label: 'Certificates ≤ 90 d',
        value: o.summary.arcExpiringWithin90Days,
        sub: 'review falling due',
        tone: o.summary.arcExpiringWithin90Days ? 'caution' : 'ok',
      })}
      ${tile({
        label: 'Planned visits',
        value: o.summary.plannedVisits,
        sub: 'maintenance booked',
      })}
      ${tile({
        label: 'Outstanding',
        value: money(o.summary.outstanding),
        sub: 'invoiced and unpaid',
        tone: o.summary.outstanding > 0 ? 'caution' : 'ok',
      })}
    </div>

    <div class="stack">${o.fleet.map(aircraftCard).join('')}</div>

    <div class="cols">
      <div class="card">
        <div class="card-h"><h2>Invoices</h2><span class="lbl">from your managing organisation</span></div>
        <div class="scrollx">
          <table class="tbl">
            <thead><tr><th>Reference</th><th class="col-opt">Period</th><th>Due</th><th class="n">Total</th><th></th></tr></thead>
            <tbody>
              ${o.invoices.length
                ? o.invoices
                    .map(
                      (i) => html`<tr>
                        <td class="n">${esc(i.reference)}</td>
                        <td class="col-opt tiny muted">
                          ${i.periodStart ? `${esc(fmtDate(i.periodStart))} – ${esc(fmtDate(i.periodEnd))}` : '—'}
                        </td>
                        <td class="tiny">${esc(fmtDate(i.dueAt))}</td>
                        <td class="n"><b>${esc(money(i.grossTotal ?? 0))}</b></td>
                        <td>
                          ${pill(
                            i.status === 'PAID' ? 'OK' : i.status === 'OVERDUE' ? 'OVERDUE' : 'DUE_SOON',
                            i.status.toLowerCase(),
                          )}
                        </td>
                      </tr>`,
                    )
                    .join('')
                : '<tr><td colspan="5" class="muted">No invoices yet.</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>

      <div class="card">
        <div class="card-h"><h2>Your agreement</h2></div>
        <div class="rows">
          ${o.contracts.length
            ? o.contracts
                .map(
                  (c) => html`
                    <div class="row">
                      <div class="bd">
                        <div class="t">${esc(c.reference)}</div>
                        <div class="m">
                          ${esc(c.basis.replace(/_/g, '.'))} · signed ${esc(fmtDate(c.signedAt))}
                          ${c.validTo ? ` · valid to ${esc(fmtDate(c.validTo))}` : ''}
                        </div>
                      </div>
                      ${c.validTo ? pill(new Date(c.validTo) > new Date() ? 'OK' : 'OVERDUE', 'Valid') : ''}
                    </div>
                  `,
                )
                .join('')
            : '<div class="row"><div class="bd"><div class="m">No management agreement on file.</div></div></div>'}
        </div>
        <div class="card-f">
          The written contract required before an organisation may manage your aircraft.
        </div>
      </div>
    </div>
  `;
}

export async function renderOwnerPortal(host: HTMLElement): Promise<void> {
  host.innerHTML = loading('Loading your aircraft');
  try {
    const o = await api.owner.overview();
    host.innerHTML = view(o);
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load your aircraft.');
  }
}
